// 規則裁判：吃 / 碰 / 槓 合法性判定
// 對應 PRD §3.2.1 規則裁判、§6.1 動作規則、§14.1 測試項
//
// 純函式集合：給定當下手牌與被質詢的牌，回答「可以做這個動作嗎、有幾種選法」。
// 不變動 hand，不發事件，不負責優先序判斷（PRD §5.2 優先序由 CRound 狀態機處理）。
//
// 設計原則：
// - 每種動作對應一個 findXxxOption(s) 函式
// - 多種組合可能時（吃可有多組）回傳陣列；只有一種或零種時回傳 option | null
// - 都用 isSameKind / tileKey 比對，不依賴 id（規則上同種牌一律等價）

import type { THand } from './hand';
import type { TMeld } from './meld';
import { isNumeric, isSameKind, tileKey, type TTile } from './tile';
import type { TSeat } from './types';
import { MeldType } from '@core/meld';

// ============================================================
// 座位關係輔助
// ============================================================

/**
 * 判斷 fromSeat 是否為 mySeat 的上家。
 *
 * 出牌順序為 0 → 1 → 2 → 3 → 0（PRD §13.4，TSeat 編號從玩家順時針）。
 * 因此 mySeat 的上家是 (mySeat - 1 + 4) % 4。
 *
 * 用途：吃牌只能吃上家。
 */
export const isUpstreamOf = (mySeat: TSeat, fromSeat: TSeat): boolean =>
  (mySeat - 1 + 4) % 4 === fromSeat;

// ============================================================
// 各種動作的選項型別
// ============================================================

/**
 * 吃的一個可能組合。
 * - `handTiles`：要從手牌取出的兩張
 * - `sequence`：完成後的順子（含被吃的捨牌），已依 rank 排序
 */
export type TChiOption = {
  readonly handTiles: readonly [TTile, TTile];
  readonly sequence: readonly [TTile, TTile, TTile];
};

/** 碰的可能組合（最多一個，因為手中至少 2 張即成立）。 */
export type TPongOption = {
  readonly handTiles: readonly [TTile, TTile];
};

/** 明槓的可能組合（最多一個，手中正好 3 張）。 */
export type TKongExposedOption = {
  readonly handTiles: readonly [TTile, TTile, TTile];
};

/** 暗槓的可能組合（手中已有 4 張同種，無需他人捨牌觸發）。 */
export type TKongConcealedOption = {
  readonly handTiles: readonly [TTile, TTile, TTile, TTile];
};

/**
 * 加槓的可能組合（自摸到第 4 張，且該種牌已碰過）。
 * - `pongMeldIndex`：對應 hand.melds 中的 pong 索引
 * - `drawnTile`：剛抓進的那張（會與 pong 的 3 張合併成槓）
 */
export type TKongAddedOption = {
  readonly pongMeldIndex: number;
  readonly drawnTile: TTile;
};

// ============================================================
// 工具：依「同種牌 key」分組計數
// ============================================================

/**
 * 把一組牌依 tileKey 分組。
 * 例：[1萬, 1萬, 5筒] → Map { 'man-1' → [t1, t2], 'pin-5' → [t3] }
 *
 * 用於快速找出「同種牌有幾張」與「這幾張是哪幾張」。
 */
const groupByKind = (tiles: readonly TTile[]): Map<string, TTile[]> => {
  const map = new Map<string, TTile[]>();
  for (const t of tiles) {
    const k = tileKey(t);
    const arr = map.get(k);
    if (arr) arr.push(t);
    else map.set(k, [t]);
  }
  return map;
};

// ============================================================
// 碰
// ============================================================

/**
 * 是否可碰：手中至少有 2 張與 `tile` 同種的牌。
 *
 * 回傳要從手牌取出的 2 張（取找到的前兩張 id）。
 * 不可碰時回傳 null。
 */
export const findPongOption = (hand: THand, tile: TTile): TPongOption | null => {
  const same = hand.concealed.filter((t) => isSameKind(t, tile));
  if (same.length < 2) return null;
  return { handTiles: [same[0] as TTile, same[1] as TTile] };
};

// ============================================================
// 明槓（直槓）：手中有 3 張同種，他人捨第 4 張
// ============================================================

export const findKongExposedOption = (hand: THand, tile: TTile): TKongExposedOption | null => {
  const same = hand.concealed.filter((t) => isSameKind(t, tile));
  if (same.length < 3) return null;
  return { handTiles: [same[0] as TTile, same[1] as TTile, same[2] as TTile] };
};

// ============================================================
// 暗槓：手中已有 4 張同種，自摸後可宣告
// ============================================================

/**
 * 列出手中所有可暗槓的組合（同種 4 張）。
 * 一手最多 3 組（4×3=12 + 雀頭 2 + 1 抓進 = 15，剛好滿）。
 *
 * 回傳所有選項供玩家挑（MVP AI 一律暗槓，PRD §7.3）。
 */
export const findKongConcealedOptions = (hand: THand): TKongConcealedOption[] => {
  const grouped = groupByKind(hand.concealed);
  const options: TKongConcealedOption[] = [];
  for (const same of grouped.values()) {
    if (same.length === 4) {
      options.push({
        handTiles: [
          same[0] as TTile,
          same[1] as TTile,
          same[2] as TTile,
          same[3] as TTile,
        ],
      });
    }
  }
  return options;
};

// ============================================================
// 加槓（續槓）：已碰過某種牌，自摸到第 4 張時可加成槓
// ============================================================

/**
 * 自摸 `drawnTile` 後是否可加槓：檢查 hand.melds 中是否有 pong 對應同種牌。
 *
 * 找到時回傳該 pong 的索引；找不到回傳 null。
 */
export const findKongAddedOption = (hand: THand, drawnTile: TTile): TKongAddedOption | null => {
  for (let i = 0; i < hand.melds.length; i++) {
    const m = hand.melds[i] as TMeld;
    if (m.type !== MeldType.Pong) continue;
    // 碰的 3 張都是同種，比對第一張即可
    const first = m.tiles[0];
    if (first && isSameKind(first, drawnTile)) {
      return { pongMeldIndex: i, drawnTile };
    }
  }
  return null;
};

// ============================================================
// 吃
// ============================================================

/**
 * 列出所有可吃的組合（僅可吃上家的捨牌、僅數牌可吃）。
 *
 * 給定捨牌 X（rank=r），可能的順子：
 *   1. (X-2, X-1, X)   → 手中需有 r-2 與 r-1
 *   2. (X-1, X, X+1)   → 手中需有 r-1 與 r+1
 *   3. (X, X+1, X+2)   → 手中需有 r+1 與 r+2
 *
 * 邊界：r=1 時 (1) 與 (2) 不成立；r=2 時 (1) 不成立；r=8 時 (3) 不成立；r=9 時 (2) (3) 不成立。
 *
 * 同種牌可能在手中有多張，本函式只回傳「找到的前一張組合」（不展開所有 id 排列）；
 * UI 會把選擇權交給玩家，他用視覺挑選真正要打出的那兩張。
 */
export const findChiOptions = (
  hand: THand,
  tile: TTile,
  fromSeat: TSeat,
  mySeat: TSeat
): TChiOption[] => {
  // 上家檢查：吃只能吃上家
  if (!isUpstreamOf(mySeat, fromSeat)) return [];
  // 只有數牌可組順子
  if (!isNumeric(tile)) return [];

  const r = tile.rank;
  const findOne = (rank: number): TTile | undefined =>
    hand.concealed.find((t) => t.suit === tile.suit && t.rank === rank);

  const options: TChiOption[] = [];

  // 組合 1：(r-2, r-1, r)
  if (r >= 3) {
    const a = findOne(r - 2);
    const b = findOne(r - 1);
    if (a && b) {
      options.push({
        handTiles: [a, b],
        sequence: [a, b, tile],
      });
    }
  }

  // 組合 2：(r-1, r, r+1)
  if (r >= 2 && r <= 8) {
    const a = findOne(r - 1);
    const b = findOne(r + 1);
    if (a && b) {
      options.push({
        handTiles: [a, b],
        sequence: [a, tile, b],
      });
    }
  }

  // 組合 3：(r, r+1, r+2)
  if (r <= 7) {
    const a = findOne(r + 1);
    const b = findOne(r + 2);
    if (a && b) {
      options.push({
        handTiles: [a, b],
        sequence: [tile, a, b],
      });
    }
  }

  return options;
};

// ============================================================
// 一站式：他人捨牌時，列出我方所有可反應動作
// ============================================================

/**
 * 對應的反應種類（不含胡，胡的偵測在 S1.3 hu.ts）。
 *
 * CRound 狀態機收到 `tile.discarded` 事件時，會對其他三家各呼叫此函式，
 * 拿到的選項清單再依 PRD §5.2 優先序處理。
 */
/** 對他人捨牌可做的反應種類 const（不含胡，胡的偵測在 hu.ts）。 */
export const DiscardReactionType = {
  Chi: 'chi',
  Pong: 'pong',
  KongExposed: 'kong-exposed',
} as const;

export type TDiscardReactionType = (typeof DiscardReactionType)[keyof typeof DiscardReactionType];

export type TDiscardReaction =
  | { readonly type: typeof DiscardReactionType.Chi; readonly option: TChiOption }
  | { readonly type: typeof DiscardReactionType.Pong; readonly option: TPongOption }
  | { readonly type: typeof DiscardReactionType.KongExposed; readonly option: TKongExposedOption };

/**
 * 列出某家對某張捨牌可做的所有反應動作（吃/碰/明槓）。
 *
 * 回傳順序固定：碰 → 明槓 → 吃（共 0..N 個）；UI 顯示與優先序處理另行決定。
 */
export const findDiscardReactions = (
  hand: THand,
  tile: TTile,
  fromSeat: TSeat,
  mySeat: TSeat
): TDiscardReaction[] => {
  const out: TDiscardReaction[] = [];

  const pong = findPongOption(hand, tile);
  if (pong) out.push({ type: DiscardReactionType.Pong, option: pong });

  const kong = findKongExposedOption(hand, tile);
  if (kong) out.push({ type: DiscardReactionType.KongExposed, option: kong });

  for (const chi of findChiOptions(hand, tile, fromSeat, mySeat)) {
    out.push({ type: DiscardReactionType.Chi, option: chi });
  }

  return out;
};
